import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, User as UserIcon, Users } from "lucide-react";

/** Team management panel — surfaced in /account for both PMs and NTN staff.
 *
 *  Phase D (2026-02): Strictly read-only for client users. Client user
 *  administration is NTN-owned; there is NO client permission that can
 *  turn on Add/Invite/Remove/Reset/Deactivate/RoleChange controls on
 *  this surface. NTN staff manage users from the Phase-C admin UI
 *  (/admin/clients/:companyId).
 */
export default function TeamPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await apiClient.get("/team/users");
      setUsers(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-8" data-testid="team-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0A192F]" />
            <h2 className="font-serif text-2xl text-[#0A192F]">Your team</h2>
          </div>
          <p className="text-sm text-[#4A5568] mt-1.5 max-w-xl" data-testid="team-panel-info">
            People in <strong>{user?.company}</strong> who can access your screenings and exchange
            notes with NTN via the Case Communication Log.
          </p>
          <p className="text-xs text-[#4A5568] mt-2 max-w-xl italic" data-testid="team-panel-managed-by-ntn">
            Client user administration is managed by NTN. Please contact NTN support to add,
            remove, or change access for a teammate.
          </p>
        </div>
      </div>

      {error && <div className="text-[#EF4444] text-sm mb-4" data-testid="team-error">{error}</div>}

      <div className="border border-[#0A192F]/10 rounded-sm divide-y divide-[#0A192F]/8">
        {users.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#4A5568]">No teammates yet.</div>
        ) : (
          users.map((u) => (
            <TeamRow key={u.id} u={u} self={user} />
          ))
        )}
      </div>
    </div>
  );
}

function TeamRow({ u, self }) {
  const isSelf = u.id === self?.id;
  return (
    <div className="px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`team-row-${u.id}`}>
      <div className="h-10 w-10 rounded-full bg-[#F3EFEA] flex items-center justify-center shrink-0">
        {u.account_role === "admin" ? <Shield className="h-4 w-4 text-[#0A192F]" /> : <UserIcon className="h-4 w-4 text-[#4A5568]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A192F] truncate">
          {u.name} {isSelf && <span className="text-xs text-[#4A5568] font-normal">(you)</span>}
        </div>
        <div className="text-xs text-[#4A5568] font-mono truncate">{u.email}</div>
      </div>
      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#4A5568]">
        {u.account_role}
      </span>
    </div>
  );
}
